import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  ChevronLeft,
  ChevronRight,
} from '@awazevr/design-system.components.icon'
import { MediaGallery } from '@awazevr/design-system.components.media-gallery'
import { breakpoints } from '@awazevr/design-system.utils.react'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import type { SearchCriteria } from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { OptimizelyState } from '@/context/optimizely-decisions-context'
import { usePublicAssetUrl } from '@/utils/helpers'

import { buildTileHref } from './buildTileHref'
import { CORNWALL_PLACES_ID, type ExplorationTile } from './data'
import {
  ExplorationContainer,
  ExplorationHeading,
  ExplorationImage,
  ExplorationLabel,
  ExplorationLink,
  ExplorationNavNext,
  ExplorationNavPrev,
  ExplorationTrack,
} from './ExplorationSuggestions.style'
import { trackExplorationClick, trackExplorationImpression } from './tracking'

export interface ExplorationSuggestionsProps {
  searchedPlace: ExplorationTile
  tiles: ExplorationTile[]
  searchCriteria?: SearchCriteria
  optimizelyState: OptimizelyState
}

const buildHeading = (searchedPlace: ExplorationTile): string =>
  searchedPlace.placesId === CORNWALL_PLACES_ID
    ? 'Destinations in Cornwall'
    : `Destinations near ${searchedPlace.regionName}`

interface ExplorationTileItemProps {
  tile: ExplorationTile
  index: number
  searchCriteria?: SearchCriteria
  onClick: () => void
  onImageDone: () => void
}

const ExplorationTileItem = ({
  tile,
  index,
  searchCriteria,
  onClick,
  onImageDone,
}: ExplorationTileItemProps): JSX.Element => {
  const imageUrl = usePublicAssetUrl(tile.imageSrc)

  return (
    <ExplorationLink
      data-index={index}
      href={buildTileHref(tile, searchCriteria)}
      aria-label={tile.regionName}
      data-testid={`exploration-tile-${tile.placesId}`}
      draggable={false}
      onClick={onClick}
    >
      <ExplorationImage
        src={imageUrl}
        alt={tile.regionName}
        draggable={false}
        width={238}
        height={124}
        onLoad={onImageDone}
        onError={onImageDone}
      />
      <ExplorationLabel>{tile.regionName}</ExplorationLabel>
    </ExplorationLink>
  )
}

export const ExplorationSuggestions = ({
  searchedPlace,
  tiles,
  searchCriteria,
  optimizelyState,
}: ExplorationSuggestionsProps): JSX.Element | null => {
  const tms = useContext(TmsContext)

  const lastImpressionKeyRef = useRef<string | null>(null)

  // Reveal only when ready (swiper + images), avoiding a staggered load.
  const [swiperReady, setSwiperReady] = useState(false)
  const [loadedImages, setLoadedImages] = useState(0)
  const [revealTimedOut, setRevealTimedOut] = useState(false)

  // Stable per-content key — `tiles` is a fresh array each parent render, so
  // keying effects on the reference would reset them on every render.
  const tilesKey = tiles.map((tile) => tile.placesId).join(',')

  useEffect(() => {
    setLoadedImages(0)
    setRevealTimedOut(false)
    const fallback = globalThis.setTimeout(() => setRevealTimedOut(true), 1500)
    return () => globalThis.clearTimeout(fallback)
  }, [tilesKey])

  const handleImageDone = useCallback(() => {
    setLoadedImages((count) => count + 1)
  }, [])

  const imagesReady = tiles.length > 0 && loadedImages >= tiles.length
  const ready = revealTimedOut || (swiperReady && imagesReady)

  useEffect(() => {
    // Rendering the carousel is the impression; fire once per distinct carousel.
    if (tiles.length === 0) {
      return
    }

    const impressionKey = `${searchedPlace.placesId}:${tilesKey}`

    if (lastImpressionKeyRef.current === impressionKey) {
      return
    }

    lastImpressionKeyRef.current = impressionKey
    trackExplorationImpression(searchedPlace, optimizelyState, tms)
  }, [searchedPlace, tilesKey, optimizelyState, tms])

  if (tiles.length === 0) {
    return null
  }

  const heading = buildHeading(searchedPlace)

  return (
    <ExplorationContainer
      $ready={ready}
      aria-label={heading}
      data-testid="exploration-suggestions"
    >
      <ExplorationHeading>{heading}</ExplorationHeading>
      <ExplorationTrack>
        <MediaGallery
          onSwiperInit={() => setSwiperReady(true)}
          swiperConfig={{
            slidesPerView: 'auto',
            spaceBetween: 12,
            grabCursor: true,
            speed: 250,
            navigation: {
              enabled: true,
              prevEl: '.exploration-nav-prev',
              nextEl: '.exploration-nav-next',
            },
            preventClicks: true,
            preventClicksPropagation: true,
            watchOverflow: true,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            breakpoints: {
              [breakpoints[0]]: {
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 20,
              },
            },
          }}
        >
          {tiles.map((tile, index) => (
            <ExplorationTileItem
              key={tile.placesId}
              tile={tile}
              index={index}
              searchCriteria={searchCriteria}
              onImageDone={handleImageDone}
              onClick={() =>
                trackExplorationClick(
                  searchedPlace,
                  tile,
                  index + 1,
                  optimizelyState,
                  tms,
                )
              }
            />
          ))}
        </MediaGallery>
        <ExplorationNavPrev
          type="button"
          className="exploration-nav-prev"
          aria-label="Previous destinations"
        >
          <ChevronLeft iconColour="ColorWhiteOpacity100" />
        </ExplorationNavPrev>
        <ExplorationNavNext
          type="button"
          className="exploration-nav-next"
          aria-label="Next destinations"
        >
          <ChevronRight iconColour="ColorWhiteOpacity100" />
        </ExplorationNavNext>
      </ExplorationTrack>
    </ExplorationContainer>
  )
}
